import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "@/lib/orderApi";

/**
 * The orders currently running on the floor, kept fresh over realtime.
 *
 * Extracted from useFloorActions so that every screen that decides whether a table
 * is free — CaptainPAD's grid, the floor plan, the billing table picker — reads the
 * same live relation instead of the denormalised `tables.status` cache, which drifts
 * (see lib/occupancy.js).
 */
export function useActiveOrders(token, subscribeRealtime) {
  const [orders, setOrders] = useState([]);
  // False until the first answer lands: before it, "no order on this table" means
  // "not read yet", not "empty".
  const [loaded, setLoaded] = useState(false);

  // One read at a time, plus at most one queued behind it. A phone whose Wi-Fi
  // flickered got every missed update replayed at once and asked for the whole
  // list once per update: ~45 identical reads in a second (C05, 24 Oct 21:59).
  // The queued read starts after the one in flight, so it still sees them all.
  const inFlight = useRef(null);
  const queued = useRef(null);
  const latestRead = useRef(null);

  const readOnce = useCallback(async () => {
    if (!token) return;
    try {
      const rows = await api.listActiveOrders(token);
      // A mocked or half-upgraded API can answer with something that is not a list;
      // `orders` is indexed on every render, so it must stay an array.
      setOrders(Array.isArray(rows) ? rows : []);
      setLoaded(true);
    } catch {
      /* the floor keeps its last known state; callers report their own errors */
    }
  }, [token]);
  latestRead.current = readOnce;

  // A new sign-in starts clean: a read queued under the old token must not
  // stand in for the new one's.
  useEffect(() => {
    inFlight.current = null;
    queued.current = null;
  }, [token]);

  const refresh = useCallback(() => {
    if (inFlight.current) {
      if (!queued.current) {
        queued.current = inFlight.current.then(() => {
          queued.current = null;
          inFlight.current = latestRead.current().finally(() => { inFlight.current = null; });
          return inFlight.current;
        });
      }
      return queued.current;
    }
    inFlight.current = readOnce().finally(() => { inFlight.current = null; });
    return inFlight.current;
  }, [readOnce]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-read every minute as well (2026-10-24): realtime says when an order
  // changes, but in a quiet room nothing changes, so "Not sent 5m" never
  // appeared and the table timers stood still.
  useEffect(() => {
    const timer = setInterval(refresh, 60_000);
    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    if (typeof subscribeRealtime !== "function") return undefined;
    return subscribeRealtime(event => {
      if (event.type === "order_update" || event.type === "resync") refresh();
    });
  }, [refresh, subscribeRealtime]);

  const orderForTable = useCallback(
    tableNumber => orders.find(row => row.table_number === tableNumber) || null,
    [orders]
  );

  return { orders, orderForTable, refresh, loaded };
}
